<template>
  <div
    ref="searchContainer"
    class="relative -mt-4 pt-4 md:-mt-8 md:pt-8"
  >
    <AisInstantSearch
      :search-client="searchClient"
      :routing="routing"
      :index-name="indexName"
      :middlewares="middlewares"
      @state-change="onStateChange"
    >
      <AisConfigure
        :hits-per-page.camel="20"
        :filters="filters"
      />
      <LayoutFacetedSearch
        :is-filtered-by-category="isFilteredByCategory"
      >
        <template #summary>
          <FilterSummary
            :index-prefix="indexPrefix"
            :search-type="searchType"
            :index-name="indexName"
          />
        </template>

        <template #filters>
          <div class="mb-5">
            <h2 class="sr-only">
              Refine your search
            </h2>
            <DebouncedSearchBox
              :delay="200"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="none"
              placeholder="Search..."
              spellcheck="false"
            />
          </div>

          <div class="max-lg:hidden">
            <FilterSidebar />
          </div>

          <FilterAccordion class="lg:hidden">
            <FilterSidebar />
          </FilterAccordion>
        </template>

        <template #sidebar>
          <slot name="sidebar" />
        </template>

        <template #results>
          <div class="flex flex-col gap-y-16">
            <AisStateResults>
              <template #default="{ results: { hits, query } }">
                <AisHits
                  v-if="hits.length > 0"
                  :class-names="{
                    'ais-Hits-list': 'col-span-12 pb-5',
                  }"
                >
                  <template #default="{ items }">
                    <div class="grid grid-cols-1 gap-y-9 gap-x-8 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-12">
                      <template
                        v-for="item in items"
                        :key="item.objectID"
                      >
                        <component
                          :is="hitComponent(item)"
                          :hit="item"
                        />
                      </template>
                    </div>
                  </template>
                </AisHits>
                <div
                  v-else
                  class="py-8 text-center text-gray-blue-800"
                >
                  No results have been found
                  <template v-if="query">
                    for <strong><q>{{ query }}</q></strong>
                  </template>
                </div>
                <ProductsPagination />
              </template>
            </AisStateResults>

            <div class="lg:hidden">
              <slot name="sidebar" />
            </div>
          </div>
        </template>
      </LayoutFacetedSearch>
    </AisInstantSearch>
  </div>
  <QuickViewModal v-if="quickViewModal" />
</template>

<script lang="ts">
export default { name: 'SiteSearch' }
</script>

<script setup lang="ts">
import { ref, watch } from 'vue'

import UiState from 'instantsearch.js/es/types/ui-state'

import { createHistoryRecord } from 'swup'

import { AisInstantSearch, AisConfigure, AisStateResults, AisHits } from 'vue-instantsearch/vue3/es'

import { liteClient } from 'algoliasearch/lite'
import connectRefinementList from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import connectPagination from 'instantsearch.js/es/connectors/pagination/connectPagination'
import connectSortBy from 'instantsearch.js/es/connectors/sort-by/connectSortBy'

import { history as historyRouter } from 'instantsearch.js/es/lib/routers'

import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings'
import { storeToRefs } from 'pinia'

import { useSearchStore } from '~/store/modules/search'
import { useUiStore } from '~/store/modules/ui'

import {
  handleToIndexName,
  getSortByOptionsBySearchType,
} from '~/components/search/helpers/indexHelper'

import LayoutFacetedSearch from './layouts/LayoutFacetedSearch.vue'
import DefaultHit from './hits/DefaultHit.vue'
import PlainText from './hits/PlainText.vue'
import BlogHit from './hits/BlogHit.vue'
import ProductHit from './hits/ProductHit.vue'
import CategoryHit from './hits/CategoryHit.vue'
import FilterSidebar from './partials/FilterSidebar.vue'
import FilterSummary from './partials/FilterSummary.vue'
import FilterAccordion from './partials/FilterAccordion.vue'
import ProductsPagination from './partials/ProductsPagination.vue'
import QuickViewModal from './partials/QuickViewModal.vue'
import DebouncedSearchBox from './partials/DebouncedSearchBox.vue'

import useScrollIntoView  from '~/composables/useScrollIntoView'

import { Hit } from '~/types/Hit'
import { SearchType } from '~/components/search/enums/SearchType'
import { AlgoliaIndices } from '~/components/search/enums/AlgoliaIndices'


const props = defineProps<{
  indexName: string,
  indexPrefix: string,
  appId: string,
  apiKey: string,
  filters: string,
  encDict: string,
  encHiddenFacets?: string,
  encInitialUiState?: string,
  searchType: SearchType,
  hitsStyle?: string,
}>()

const decode = (str ?: string) => str ? JSON.parse(atob(str)) : null
const dictionary = decode(props.encDict) ?? {}
const hiddenFacets = decode(props.encHiddenFacets) ?? []
const algoliaUiState: UiState = decode(props.encInitialUiState) ?? {}


const uiStore = useUiStore()
const { quickViewModal } = storeToRefs(uiStore)

const searchStore = useSearchStore()
searchStore.setDictionary(dictionary)
searchStore.setHiddenFacets(hiddenFacets)
searchStore.setSearchType(props.searchType)

const routing = {
  router: historyRouter({
    push: url => createHistoryRecord(url),
  }),
  stateMapping: singleIndexMapping(props.indexName),
}

const searchClient = liteClient(
  props.appId,
  props.apiKey,
)


/* @see https://www.algolia.com/doc/api-reference/widgets/middleware/vue/ */
const middlewares = [
  /**
   * Set the initial state of the search from Craft - used for category pages
   *
   * @param instantSearchInstance
   */
  function setInitialState({ instantSearchInstance }) {

    // InstantSearch requires that a widget exists for an attribute before we set state for it
    // so we create a virtual widget for each refinementList attribute that doesn't render anything
    // but ensures that the attribute exists in the InstantSearch internal store
    const virtualRefinementList = connectRefinementList(() => null)
    const virtualPagination = connectPagination(() => null)
    const virtualSortBy = connectSortBy(() => null)

    const virtualWidgets = Object.keys(algoliaUiState?.refinementList ?? {})
      .map(attribute => virtualRefinementList({ attribute }))

    virtualWidgets.push(virtualPagination())
    virtualWidgets.push(virtualSortBy({ items: getSortByOptionsBySearchType(props.searchType, props.indexPrefix) }))

    instantSearchInstance.addWidgets(virtualWidgets)


    // We wait until InstantSearch has started before merging in the state from Craft
    watch(() => instantSearchInstance.started, () => {
      const currentState = instantSearchInstance.getUiState()[props.indexName]

      instantSearchInstance.setUiState({
        [props.indexName]: {
          ...algoliaUiState,
          // Current state last so that URL/user state overrides the state from Craft
          ...currentState,
        },
      })
    })
  },
  function storeSearchReady({ instantSearchInstance }) {
    watch(() => instantSearchInstance.started, () => {
      searchStore.setIsReady(true)
    })
  },
]

const searchContainer = ref<HTMLDivElement | null>(null)
const onStateChange = ({ uiState, setUiState }) => {
  const state = uiState[props.indexName]

  const {
    sortBy,
    query,
  } = state

  // don't order by best match if there is no query
  if (!query && sortBy === handleToIndexName(props.indexPrefix, AlgoliaIndices.PRIMARY)) {
    state.sortBy = props.indexName
  }
  // if there is a query and order hasn't been explicitly set, set it to best match
  if (query && !sortBy) {
    state.sortBy = handleToIndexName(props.indexPrefix, AlgoliaIndices.PRIMARY)
  }

  // Update the store with the new state
  searchStore.setAisState(uiState[props.indexName])

  // scroll to top of component when search state changes, but not immediately after page load
  if (searchStore.isReady) {
    useScrollIntoView(searchContainer.value)()
  }

  setUiState(uiState)
}

const isFilteredByCategory = !!(algoliaUiState?.refinementList
  && Object.values(algoliaUiState.refinementList).some(
    (refinement: string[]) => refinement.length > 0,
  ))

const hitComponent = (hit: Hit) => {
  if(props.hitsStyle == 'textonly') {
    return PlainText
  }
  if (hit.isProduct) {
    return ProductHit
  } else if (hit.isBlog) {
    return BlogHit
  } else if (hit.isCategory) {
    return CategoryHit
  }
  return DefaultHit
}
</script>
